import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';

const storySchema = z.object({
  category: z.enum(['older_adult', 'family_member', 'caregiver', 'professional'], {
    required_error: 'Please select a category.',
  }),
  title: z.string().min(5, 'Title must be at least 5 characters').max(100, 'Title must be less than 100 characters'),
  content: z.string().min(50, 'Story must be at least 50 characters').max(2000, 'Story must be less than 2000 characters'),
  author_name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  author_email: z.string().email('Please enter a valid email address'),
  author_location: z.string().max(100, 'Location must be less than 100 characters').optional(),
  author_role: z.string().max(100, 'Role must be less than 100 characters').optional(),
  consent_for_publication: z.boolean().refine(val => val === true, {
    message: 'You must consent to publication to submit your story.',
  }),
});

type StoryFormData = z.infer<typeof storySchema>;

interface StorySubmissionFormProps {
  onSuccess?: () => void;
}

export const StorySubmissionForm = ({ onSuccess }: StorySubmissionFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<StoryFormData>({
    resolver: zodResolver(storySchema),
    defaultValues: {
      category: undefined,
      title: '',
      content: '',
      author_name: '',
      author_email: '',
      author_location: '',
      author_role: '',
      consent_for_publication: false,
    },
  });

  const onSubmit = async (data: StoryFormData) => {
    setIsSubmitting(true);
    try {
      // Capture referral code and UTM parameters
      const params = new URLSearchParams(window.location.search);
      const referralCode = params.get('ref') || 
                          params.get('referral') || 
                          localStorage.getItem('referral_code') || 
                          '';
      
      const payload = {
        category: data.category,
        title: data.title,
        content: data.content,
        authorName: data.author_name,
        authorEmail: data.author_email,
        location: data.author_location || '',
        role: data.author_role || '',
        consent: data.consent_for_publication,
        pagePath: window.location.pathname,
        referrer: document.referrer || '',
        utmSource: params.get('utm_source') || sessionStorage.getItem('utm_source') || '',
        utmMedium: params.get('utm_medium') || sessionStorage.getItem('utm_medium') || '',
        utmCampaign: params.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || '',
        utmContent: params.get('utm_content') || sessionStorage.getItem('utm_content') || '',
        utmTerm: params.get('utm_term') || sessionStorage.getItem('utm_term') || '',
        referralCode,
        source: 'website'
      };

      const { data: result, error } = await supabase.functions.invoke('submit-story', {
        body: payload
      });

      if (error) throw error;

      if (result && !result.success) {
        throw new Error(result.message || 'Submission failed');
      }

      toast.success('Story submitted successfully! We\'ll review it and may feature it on our website.');
      form.reset();
      onSuccess?.();
    } catch (error) {
      console.error('Error submitting story:', error);
      toast.error('Failed to submit story. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const categoryOptions = [
    { value: 'older_adult', label: 'Older Adult' },
    { value: 'family_member', label: 'Family Member' },
    { value: 'caregiver', label: 'Caregiver' },
    { value: 'professional', label: 'Healthcare Professional' },
  ];

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PaperAirplaneIcon className="w-6 h-6 text-primary" />
          Share Your Story
        </CardTitle>
        <p className="text-muted-foreground">
          Help inspire others by sharing your experience with aging, caregiving, or supporting loved ones.
        </p>
      </CardHeader>
      
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Category Selection */}
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>I am a...</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categoryOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Story Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Story Title</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Give your story a compelling title..." 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    A brief, descriptive title for your story (5-100 characters)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Story Content */}
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Story</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Share your experience, challenges, successes, or insights about aging, caregiving, or supporting loved ones..."
                      className="min-h-[120px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Tell us about your experience (50-2000 characters). {form.watch('content')?.length || 0}/2000
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Author Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="author_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Name</FormLabel>
                    <FormControl>
                      <Input placeholder="First and last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="author_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      So we can follow up if your story is featured
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="author_location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="City, State" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="author_role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role/Title (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Retired Teacher, Home Health Aide, Daughter" {...field} />
                  </FormControl>
                  <FormDescription>
                    Help readers understand your perspective
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Consent Checkbox */}
            <FormField
              control={form.control}
              name="consent_for_publication"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Publication Consent
                    </FormLabel>
                    <FormDescription>
                      I consent to having my story potentially featured on the SeniorThrive website and marketing materials to help inspire and educate others. My story may be edited for length and clarity.
                    </FormDescription>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Story'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};